import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useApplications } from '@/hooks/useApplications'
import { createApplication } from '@/api/tapApplications'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import {
  TapApplication,
  TapApplicationListParams,
  TapStage,
  TapStatusFlag,
  TapApplicationType,
} from '@/types/index'
import { Search, AlertCircle } from 'lucide-react'
import { DEFAULT_PAGE_SIZE } from '@/api/paging'
import StageBadge from './components/StageBadge'
import StatusFlagBadge from './components/StatusFlagBadge'
import SlaIndicator from './components/SlaIndicator'

// Feature #199: Decision renamed to Outcome.
const ALL_STAGES: TapStage[] = ['Review', 'PhoneScreen', 'Interview', 'Assessment', 'ReferenceCheck', 'Offer', 'Outcome']
// Feature #223: outcome flags (Successful / Unsuccessful / TalentPool) are
// also valid filter targets — they sit alongside the operational flags so
// recruiters can filter the list by an applied outcome flag.
const ALL_FLAGS: TapStatusFlag[] = ['Urgent', 'OnHold', 'Withdrawn', 'Archived', 'Successful', 'Unsuccessful', 'TalentPool']
const ALL_TYPES: TapApplicationType[] = ['JobApplication', 'ExpressionOfInterest']

const stageLabels: Record<string, string> = {
  Review: 'Review',
  PhoneScreen: 'Phone Screen',
  Interview: 'Interview',
  Assessment: 'Assessment',
  ReferenceCheck: 'Reference Check',
  Offer: 'Offer',
  Outcome: 'Outcome',
}

// WI #337: sorting is done by the server, so a column is only sortable if
// the backend can express it as an ORDER BY over the whole dataset. A
// client-side comparator would order the current page and present itself as
// having ordered everything.
//
// Bug #338 added an explicit sort allow-list to `list_applications`, so
// `applicant`, `assignedTo` and `stage` are sortable again — they resolve
// server-side to the applicant's surname, the assignee's display name, and
// workflow order respectively, not to the raw stored value.
//
// Still deliberately absent:
//   slaStatus — computed per row in the response from the SLA feature flag,
//               the per-stage SLA config and the last stage change. It is
//               not a column and reproducing that ladder in SQL would mean
//               keeping two copies of the rule in step. The backend refuses
//               `sort_by=sla_status` with a 400 rather than silently sorting
//               by something else, so this must stay out of the map.
//
// Anything added here must exist in the backend allow-list; an unknown key
// is now a 400, not a silent fallback.
const SORT_FIELDS: Record<string, string> = {
  referenceNumber: 'reference_number',
  applicant: 'applicant',
  stage: 'stage',
  statusFlag: 'status_flag',
  receivedAt: 'received_at',
  assignedTo: 'assigned_to',
}

const typeLabels: Record<string, string> = {
  JobApplication: 'Job Application',
  ExpressionOfInterest: 'Expression of Interest',
}

const ApplicationList: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [stageFilter, setStageFilter] = useState<string>(searchParams.get('stage') || '')
  const [flagFilter, setFlagFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  // Feature #178: column header sort state.
  const [sortBy, setSortBy] = useState<string>('receivedAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  // Feature #148: the New Application modal now leads with the applicant, not
  // the position. `positionTitle` is kept in state so recruiters can still
  // optionally attach a role, but it's no longer required and no longer the
  // primary field.
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    applicationType: 'JobApplication' as TapApplicationType,
    positionTitle: '',
    vacancyCode: '',
    summary: '',
  })

  // Sync stage filter from URL params. Clear the filter when the URL has no
  // stage so navigating "All Applications" from a stage-filtered menu item
  // (e.g. /tap/applications?stage=Review → /tap/applications) actually drops
  // the filter instead of stickily showing the previous stage.
  //
  // Bug #235: also reset the Flag filter on every nav-menu transition.
  // Previously only the Stage filter resynced from the URL — a user who
  // had set "Talent Pool" while looking at All Applications would still
  // see that Flag filter applied after clicking Interview, and an empty
  // result list would confuse them. Resetting it on every searchParams
  // change matches the user's stated mental model ("a new menu item is
  // a fresh start").
  useEffect(() => {
    const urlStage = searchParams.get('stage')
    if (urlStage && ALL_STAGES.includes(urlStage as TapStage)) {
      setStageFilter(urlStage)
    } else {
      setStageFilter('')
    }
    setFlagFilter('')
    setPage(1)
  }, [searchParams])

  const [page, setPage] = useState(1)

  // WI #337: every filter, search or sort change produces a different result
  // set, so the page number goes back to 1 alongside it — otherwise narrowing
  // a filter while on page 4 shows an empty table. Resetting in the same tick
  // as the change (rather than in a follow-up effect) keeps it to one request.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // WI #337: memoised — `useApplications` re-fetches on this object's identity.
  // Filters and search are query params now; `list_applications` searches
  // applicant name/email, reference number and position title.
  const listParams = useMemo<TapApplicationListParams>(
    () => ({
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: SORT_FIELDS[sortBy] ?? 'received_at',
      sortOrder,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(stageFilter ? { stage: stageFilter as TapStage } : {}),
      ...(flagFilter ? { statusFlag: flagFilter as TapStatusFlag } : {}),
      ...(typeFilter ? { applicationType: typeFilter as TapApplicationType } : {}),
    }),
    [page, debouncedSearch, stageFilter, flagFilter, typeFilter, sortBy, sortOrder],
  )

  const { applications, total, isLoading, error, refetch } = useApplications(listParams)

  // WI #337: the client-side filter and sort that used to live here were
  // applied to whichever 20 rows the server happened to return. Both are
  // query params now, so `applications` is already the filtered, sorted page.

  // Feature #178: sort handler for DataTable column headers. The key is a
  // UI column key; SORT_FIELDS maps it to the backend column.
  const handleSort = (key: string, order: 'asc' | 'desc') => {
    setSortBy(key)
    setSortOrder(order)
    setPage(1)
  }

  // Reset to the person-first default shape after a successful create / close.
  const resetForm = () => setFormData({
    firstName: '',
    lastName: '',
    email: '',
    applicationType: 'JobApplication',
    positionTitle: '',
    vacancyCode: '',
    summary: '',
  })

  const handleCreateApplication = async () => {
    // Feature #148: the applicant is now the required input, not the
    // position. First name, last name, and email are all mandatory because
    // the backend's ApplicantCreatePayload requires them.
    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert('First name, last name, and email are required')
      return
    }
    try {
      await createApplication({
        applicationType: formData.applicationType,
        // Optional — only pass through if the recruiter actually typed one.
        positionTitle: formData.positionTitle || undefined,
        vacancyCode: formData.vacancyCode || undefined,
        summary: formData.summary || undefined,
        applicant: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        },
      })
      setCreateModalOpen(false)
      resetForm()
      refetch()
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      alert(
        typeof detail === 'string'
          ? `Failed to create application: ${detail}`
          : 'Failed to create application',
      )
    }
  }

  const handleRowClick = (app: TapApplication) => {
    navigate(`/tap/applications/${app.id}`)
  }

  const selectStyle: React.CSSProperties = {
    padding: '8px 12px',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'var(--color-white)',
    cursor: 'pointer',
    minWidth: '160px',
  }

  const columns = [
    {
      key: 'slaStatus' as keyof TapApplication,
      header: 'SLA',
      width: '50px',
      // Bug #338: intentionally not sortable — sla_status is computed per
      // row in the response, so there is nothing to ORDER BY. See the
      // SORT_FIELDS comment above.
      sortable: false,
      render: (value: string | null) => <SlaIndicator status={value} />,
    },
    {
      key: 'referenceNumber' as keyof TapApplication,
      header: 'Ref #',
      width: '120px',
      sortable: true,
      render: (value: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--color-dark-grey)' }}>{value}</span>
      ),
    },
    {
      // Feature #148: the primary column is now the applicant, not the
      // position. We use the `applicant` key so DataTable's default sort
      // wires up; the renderer pulls first + last from the nested summary.
      key: 'applicant' as keyof TapApplication,
      header: 'Applicant',
      // Bug #338: sorts server-side by surname then forename.
      sortable: true,
      render: (_value: unknown, row: TapApplication) => {
        const fullName = [row.applicant?.firstName, row.applicant?.lastName]
          .filter(Boolean)
          .join(' ')
        return (
          <div>
            <div style={{ fontWeight: 500, color: 'var(--color-black)' }}>{fullName || '-'}</div>
            {row.applicant?.email && (
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>{row.applicant.email}</div>
            )}
          </div>
        )
      },
    },
    // Bug #182: removed the "Type" column — it showed unhelpful values
    // (External/Standard). Application Type (Job Application / Expression of
    // Interest) is visible inside the Application Detail view instead.
    {
      key: 'stage' as keyof TapApplication,
      header: 'Stage',
      // Bug #338: sorts in workflow order (Review → Outcome), not
      // alphabetically — the ordering lives in the backend allow-list.
      sortable: true,
      render: (value: string) => <StageBadge stage={value} />,
    },
    {
      key: 'statusFlag' as keyof TapApplication,
      header: 'Flag',
      sortable: true,
      render: (value: string | undefined) => <StatusFlagBadge flag={value} />,
    },
    {
      key: 'receivedAt' as keyof TapApplication,
      header: 'Received',
      sortable: true,
      render: (value: string | undefined) => formatDate(value),
    },
    {
      // Bug #168: show server-resolved display name instead of raw UUID.
      key: 'assignedTo' as keyof TapApplication,
      header: 'Assigned To',
      // Bug #338: sorts by the display name shown in the cell, not by the
      // UUID the column stores.
      sortable: true,
      render: (_value: string | undefined, row: TapApplication) =>
        row.assignedToDisplayName || '-',
    },
  ]

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="Talent Acquisition"
        actions={
          <button
            onClick={() => {
              resetForm()
              setCreateModalOpen(true)
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            + New Application
          </button>
        }
      />

      <div style={{ padding: '24px' }}>
        {error && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading applications</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error?.message ?? 'An error occurred'}</p>
            </div>
          </div>
        )}

        {/* Search and filter bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--color-dark-grey)' }} />
            <input
              type="text"
              placeholder="Search by applicant name, email, reference, or assignee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>

          <select
            value={stageFilter}
            onChange={(e) => {
              setStageFilter(e.target.value)
              setPage(1)
            }}
            style={selectStyle}
          >
            <option value="">All Stages</option>
            {ALL_STAGES.map((s) => (
              <option key={s} value={s}>{stageLabels[s]}</option>
            ))}
          </select>

          <select
            value={flagFilter}
            onChange={(e) => {
              setFlagFilter(e.target.value)
              setPage(1)
            }}
            style={selectStyle}
          >
            <option value="">All Flags</option>
            {ALL_FLAGS.map((f) => (
              <option key={f} value={f}>
                {f === 'OnHold' ? 'On Hold' : f === 'TalentPool' ? 'Talent Pool' : f}
              </option>
            ))}
          </select>

          {/* Bug #182: Type filter removed alongside the Type column. */}
        </div>

        {/* Data table */}
        {/* WI #337: only show the full-page loader on the first load. Paging
            keeps the table mounted so the footer doesn't disappear mid-click. */}
        {isLoading && applications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Loading applications...
          </div>
        ) : applications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            {searchTerm || stageFilter || flagFilter || typeFilter
              ? 'No applications match your filters.'
              : 'No applications found.'}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={applications}
            rowKey="id"
            isLoading={isLoading}
            totalItems={total}
            currentPage={page}
            pageSize={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            // Bug #166: clicking anywhere on the row now opens the detail page
            // (previously only the Actions ▸ View menu did this). The actions
            // button stops propagation internally so the menu still works.
            onRowClick={handleRowClick}
            // Feature #186: removed Actions column — row click already
            // navigates to the detail page (Bug #166).
          />
        )}
      </div>

      {/* Create modal — Feature #148 reshaped this to be person-first.
          Applicant details are mandatory; position/vacancy/summary are
          optional secondary fields. */}
      <Modal
        isOpen={createModalOpen}
        title="New Application"
        onClose={() => setCreateModalOpen(false)}
        isDirty={Boolean(
          formData.firstName ||
          formData.lastName ||
          formData.email ||
          formData.positionTitle ||
          formData.vacancyCode ||
          formData.summary ||
          formData.applicationType !== 'JobApplication'
        )}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setCreateModalOpen(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateApplication}
              disabled={!formData.firstName || !formData.lastName || !formData.email}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                opacity:
                  !formData.firstName || !formData.lastName || !formData.email ? 0.6 : 1,
              }}
            >
              Create
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                First name (required)
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Last name (required)
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Email (required)
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Application type
            </label>
            <select
              value={formData.applicationType}
              onChange={(e) => setFormData({ ...formData, applicationType: e.target.value as TapApplicationType })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
              }}
            >
              <option value="JobApplication">Job Application</option>
              <option value="ExpressionOfInterest">Expression of Interest</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Vacancy code (optional)
            </label>
            <input
              type="text"
              value={formData.vacancyCode}
              onChange={(e) => setFormData({ ...formData, vacancyCode: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Summary (optional)
            </label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                resize: 'vertical',
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ApplicationList
